import { useEffect, useState } from 'react';
import { formatPrice, type ComparedPlayer, type PlayerComparison as PlayerComparisonData } from '@fantasy/types';
import { api, ApiRequestError } from '../api.ts';
import { GameweekChart, type GameweekSeries } from './GameweekChart.tsx';
import { PlayerPicker, type PlayerPickerValue } from './PlayerPicker.tsx';

const MIN_PLAYERS = 2;
const MAX_PLAYERS = 4;

/** Fixed categorical order, one per comparison slot — same values as
 * `Tracker`'s own chart palette (kept as a local copy rather than an import,
 * since this ticket doesn't own that file), never cycled. */
const CHART_PALETTE = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'];

type Direction = 'higher' | 'lower';

/** One row of the comparison table. `direction` decides which value in the
 * row counts as "best" — higher for every signal where more is better, lower
 * for price (cheaper is the better squad-budget outcome) and mean fixture
 * difficulty (FPL's 1-5 scale, where 1 is easiest). */
interface ComparisonMetric {
  key: string;
  label: string;
  direction: Direction;
  value: (player: ComparedPlayer) => number | undefined;
  format: (player: ComparedPlayer) => string;
}

const METRICS: ComparisonMetric[] = [
  { key: 'price', label: 'Price', direction: 'lower', value: (p) => p.price, format: (p) => formatPrice(p.price) },
  { key: 'form', label: 'Form', direction: 'higher', value: (p) => p.form, format: (p) => p.form.toFixed(1) },
  {
    key: 'totalPoints',
    label: 'Total points',
    direction: 'higher',
    value: (p) => p.totalPoints,
    format: (p) => String(p.totalPoints),
  },
  {
    key: 'minutes',
    label: 'Minutes (season)',
    direction: 'higher',
    value: (p) => p.seasonMinutes,
    format: (p) => String(p.seasonMinutes),
  },
  {
    key: 'xg',
    label: 'xG',
    direction: 'higher',
    value: (p) => p.expectedGoals,
    format: (p) => p.expectedGoals?.toFixed(2) ?? '—',
  },
  {
    key: 'xa',
    label: 'xA',
    direction: 'higher',
    value: (p) => p.expectedAssists,
    format: (p) => p.expectedAssists?.toFixed(2) ?? '—',
  },
  {
    key: 'xgi',
    label: 'xGI',
    direction: 'higher',
    value: (p) => p.expectedGoalInvolvements,
    format: (p) => p.expectedGoalInvolvements?.toFixed(2) ?? '—',
  },
  {
    key: 'ict',
    label: 'ICT index',
    direction: 'higher',
    value: (p) => p.ictIndex,
    format: (p) => p.ictIndex?.toFixed(1) ?? '—',
  },
  {
    key: 'ownership',
    label: 'Ownership',
    direction: 'higher',
    value: (p) => p.selectedByPercent,
    format: (p) => (p.selectedByPercent !== undefined ? `${p.selectedByPercent.toFixed(1)}%` : '—'),
  },
  { key: 'score', label: 'Score', direction: 'higher', value: (p) => p.score, format: (p) => String(p.score) },
  {
    key: 'fixtureOutlook',
    label: 'Mean fixture difficulty',
    direction: 'lower',
    value: (p) => p.fixtureOutlook,
    format: (p) => p.fixtureOutlook.toFixed(1),
  },
];

/** The best value in a row, ignoring players missing that signal — `null`
 * when nobody being compared has it, so nothing gets marked. */
function bestValueFor(metric: ComparisonMetric, players: ComparedPlayer[]): number | null {
  const values = players.map(metric.value).filter((v): v is number => v !== undefined);
  if (values.length === 0) return null;
  return metric.direction === 'higher' ? Math.max(...values) : Math.min(...values);
}

/**
 * "Salah or Saka?" (T-051): pick two to four players with the same
 * `PlayerPicker` typeahead the squad editor uses, and see them side by side
 * — a metric-per-row table for the averages, `GameweekChart` underneath for
 * whether one of them has simply been carrying a hot month. Reuses
 * `GameweekChart` as-is (T-050's `history` is already shaped as `{
 * gameweek, points }`, the same as a watched manager's), just with player
 * identity instead of manager identity.
 */
export function PlayerComparison() {
  const [slots, setSlots] = useState<Array<PlayerPickerValue | null>>([null, null]);
  const [comparison, setComparison] = useState<PlayerComparisonData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // De-duplicated so picking the same player into two slots by mistake
  // degrades to "not enough players yet" instead of a confusing 400 for a
  // duplicate id.
  const selectedIds = [...new Set(slots.filter((s): s is PlayerPickerValue => s !== null).map((s) => s.fplId))];
  const selectedIdsKey = selectedIds.join(',');

  useEffect(() => {
    if (selectedIds.length < MIN_PLAYERS) {
      setComparison(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .comparePlayers(selectedIds)
      .then(({ comparison: fetched }) => {
        if (!cancelled) setComparison(fetched);
      })
      .catch((err) => {
        if (!cancelled) {
          setComparison(null);
          setError(err instanceof ApiRequestError ? err.message : 'Could not load this comparison.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdsKey]);

  function onSlotChange(index: number, value: PlayerPickerValue | null) {
    setSlots((prev) => prev.map((slot, i) => (i === index ? value : slot)));
  }

  function addSlot() {
    setSlots((prev) => (prev.length < MAX_PLAYERS ? [...prev, null] : prev));
  }

  function removeSlot(index: number) {
    setSlots((prev) => (prev.length > MIN_PLAYERS ? prev.filter((_, i) => i !== index) : prev));
  }

  // A player whose history fetch failed still appears in `comparison.players`
  // (the table is useful without the chart, per T-050) but is left out of
  // `chartSeries` entirely — visibly missing from the chart rather than
  // plotted as an empty, invisible line, with `comparison.warnings` below
  // explaining why.
  const chartSeries: GameweekSeries[] = (comparison?.players ?? [])
    .map((player, index) => ({
      id: player.fplId,
      label: player.name,
      color: CHART_PALETTE[index] ?? CHART_PALETTE[0] ?? '#2a78d6',
      points: comparison?.history.find((h) => h.fplId === player.fplId)?.series ?? [],
    }))
    .filter((s) => s.points.length > 0);

  return (
    <section className="panel panel--accent-2" aria-labelledby="compare-heading">
      <div className="panel-head">
        <div>
          <h3 id="compare-heading">Compare players</h3>
          <p className="hint">Pick two to four players to see them side by side.</p>
        </div>
      </div>

      <div className="comparison-pickers">
        {slots.map((slot, index) => (
          <div className="comparison-picker-slot" key={`slot-${index}`}>
            <PlayerPicker label={`Player ${index + 1}`} value={slot} onChange={(value) => onSlotChange(index, value)} />
            {slots.length > MIN_PLAYERS && (
              <button type="button" className="ghost small" onClick={() => removeSlot(index)}>
                Remove
              </button>
            )}
          </div>
        ))}
      </div>

      {slots.length < MAX_PLAYERS && (
        <button type="button" className="ghost small mt-md" onClick={addSlot}>
          Add another player
        </button>
      )}

      {selectedIds.length < MIN_PLAYERS && (
        <p className="hint mt-md">Choose at least two players above to compare them.</p>
      )}

      {loading && (
        <p className="hint mt-md" aria-live="polite">
          Loading comparison…
        </p>
      )}

      {!loading && error && (
        <ul className="notices mt-md">
          <li className="error">{error}</li>
        </ul>
      )}

      {!loading && !error && comparison && (
        <>
          {comparison.warnings.length > 0 && (
            <ul className="notices mt-md">
              {comparison.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}

          <div className="scroll-x mt-md">
            <table>
              <caption className="hint table-caption">
                The best figure in each row is marked with a ★, not colour alone.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Metric</th>
                  {comparison.players.map((player) => (
                    <th scope="col" key={player.fplId}>
                      <div>{player.name}</div>
                      <div className="hint">
                        {player.position} · {player.club}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {METRICS.map((metric) => {
                  const best = bestValueFor(metric, comparison.players);
                  return (
                    <tr key={metric.key}>
                      <th scope="row">{metric.label}</th>
                      {comparison.players.map((player) => {
                        const value = metric.value(player);
                        const isBest = best !== null && value !== undefined && value === best;
                        return (
                          <td key={player.fplId} className={isBest ? 'num comparison-best' : 'num'}>
                            {metric.format(player)}
                            {isBest && (
                              <span className="comparison-best-marker" aria-hidden="true">
                                {' '}
                                ★
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {chartSeries.length > 0 ? (
            <div className="mt-md">
              <GameweekChart series={chartSeries} />
            </div>
          ) : (
            <p className="hint mt-md">No gameweek history available for these players.</p>
          )}
        </>
      )}
    </section>
  );
}
